import { FunnelStepHeader, StepperFooter } from '@folio/components';
import { useAccessiblePayerCustomerIds } from '@folio/hooks/useAccessiblePayerCustomerIds';
import { useAssertedParams } from '@folio/lib';

import { useQueries, useSuspenseQuery } from '@tanstack/react-query';
import {
  customerControllerGetCustomerQueryOptions,
  configControllerGetPublicProductConfigQueryOptions,
  policyControllerGetPolicyQueryOptions,
  viewSpecificControllerListCustomerPaymentMethodsQueryOptions,
} from 'korint-sdk';
import { CheckIcon, CircleDashedIcon, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PaymentMethodModule } from '../PaymentMethodModule/PaymentMethodModule';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { Badge } from '@folio/components/ui/badge';

function PaymentMethodTypeBadges({
  types,
  availableTypes,
}: {
  types: string[];
  availableTypes: Set<string>;
}) {
  const { t } = useTranslation();

  const renderBadge = (type: string) => {
    const isAvailable = availableTypes.has(type);
    return (
      <Badge
        variant="light"
        tone={isAvailable ? 'success' : 'muted'}
        size="sm"
        className="gap-1"
        title={t(isAvailable ? 'paymentMethodFunnel.available' : 'paymentMethodFunnel.missing')}
      >
        {isAvailable ? <CheckIcon className="h-3 w-3" /> : <CircleDashedIcon className="h-3 w-3" />}
        {t(`paymentMethodFunnel.typeLabels.${type}`)}
      </Badge>
    );
  };

  if (types.length === 1) {
    return renderBadge(types[0]);
  }

  return (
    <Group wrap gap={0} className="gap-1.5">
      {types.map((type, index) => (
        <span key={type} className="inline-flex items-center gap-1.5">
          {renderBadge(type)}
          {index < types.length - 1 && (
            <Text inline size="xs" tone="muted">
              {t('paymentMethodFunnel.or')}
            </Text>
          )}
        </span>
      ))}
    </Group>
  );
}

type PaymentMethodFunnelModuleProps = {
  title?: string;
  subtitle?: string;
};

export function PaymentMethodFunnelModule({
  title,
  subtitle,
}: PaymentMethodFunnelModuleProps = {}) {
  const { t } = useTranslation();
  const { policyId, branchId } = useAssertedParams();

  const { data: policy } = useSuspenseQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
  });

  const { data: paymentConfig } = useSuspenseQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: ({ payment }) => payment,
  });

  const payerCustomerIds = useAccessiblePayerCustomerIds(policy);

  const payerPaymentQueries = useQueries({
    queries: payerCustomerIds.map(customerId =>
      viewSpecificControllerListCustomerPaymentMethodsQueryOptions(customerId),
    ),
  });
  const availableTypes = new Set(
    payerPaymentQueries
      .flatMap(({ data }) => data?.data ?? [])
      .map(({ paymentMethodType }) => paymentMethodType),
  );

  const requiredOnEarly = paymentConfig.paymentMethodOnEarlyPayment?.length
    ? paymentConfig.paymentMethodOnEarlyPayment
    : undefined;
  const requiredOnRecurring = paymentConfig.paymentMethodOnRecurringPayment?.length
    ? paymentConfig.paymentMethodOnRecurringPayment
    : undefined;
  const hasRequirements = !!requiredOnEarly || !!requiredOnRecurring;

  // The backend refuses OFFLINE on a multi-payer policy, so the "either add a method or
  // pick off-platform" hint only applies when a single payer can actually opt out.
  const offlineAvailable =
    (paymentConfig.allowedPaymentModes ?? []).includes('OFFLINE') && payerCustomerIds.length === 1;

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={title ?? t('funnelSteps.paymentMethod.title')}
        subtitle={
          subtitle ??
          (offlineAvailable
            ? t('funnelSteps.paymentMethod.subtitleOffline')
            : t('funnelSteps.paymentMethod.subtitle', ''))
        }
      />
      {hasRequirements && (
        <div className="rounded-lg border p-4">
          <Group gap={2} className="mb-3">
            <InfoIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <Text inline weight="medium">
              {t('paymentMethodFunnel.requirementsTitle')}
            </Text>
          </Group>
          <Stack gap={2} className="pl-6">
            {requiredOnEarly && requiredOnEarly.length > 0 && (
              <Group wrap gap={2}>
                <Text inline size="sm" tone="muted">
                  {t('paymentMethodFunnel.earlyPaymentLabel')}
                </Text>
                <PaymentMethodTypeBadges types={requiredOnEarly} availableTypes={availableTypes} />
              </Group>
            )}
            {requiredOnRecurring && requiredOnRecurring.length > 0 && (
              <Group wrap gap={2}>
                <Text inline size="sm" tone="muted">
                  {t('paymentMethodFunnel.recurringPaymentLabel')}
                </Text>
                <PaymentMethodTypeBadges
                  types={requiredOnRecurring}
                  availableTypes={availableTypes}
                />
              </Group>
            )}
          </Stack>
        </div>
      )}

      <PaymentMethodModule addButtonLabel={t('paymentMethodFunnel.addPaymentMethodCta')} />

      <ValidationFooter
        payerCustomerIds={payerCustomerIds}
        branchId={branchId}
        requiredOnEarly={requiredOnEarly}
        requiredOnRecurring={requiredOnRecurring}
      />
    </Stack>
  );
}

function ValidationFooter({
  payerCustomerIds,
  branchId,
  requiredOnEarly,
  requiredOnRecurring,
}: {
  payerCustomerIds: string[];
  branchId: string | undefined;
  requiredOnEarly: string[] | undefined;
  requiredOnRecurring: string[] | undefined;
}) {
  const customerPaymentQueries = useQueries({
    queries: payerCustomerIds.map(customerId => ({
      ...viewSpecificControllerListCustomerPaymentMethodsQueryOptions(customerId),
    })),
  });

  // Same query key as PaymentMethodModule's customer queries: the payment-mode card's refetch
  // after a switch then refreshes this footer too, instead of leaving Next greyed out.
  const customerQueries = useQueries({
    queries: payerCustomerIds.map(customerId => ({
      ...customerControllerGetCustomerQueryOptions(customerId, { branchId }),
    })),
  });

  const allSatisfied =
    payerCustomerIds.length > 0 &&
    customerPaymentQueries.every(({ data }, index) => {
      // An OFFLINE payer is never charged: no payment method can be demanded of them
      if (customerQueries[index]?.data?.paymentMode === 'OFFLINE') {
        return true;
      }

      const methods = data?.data ?? [];

      if (requiredOnEarly) {
        if (!requiredOnEarly.some(type => methods.some(m => m.paymentMethodType === type))) {
          return false;
        }
      }

      if (requiredOnRecurring) {
        if (!requiredOnRecurring.some(type => methods.some(m => m.paymentMethodType === type))) {
          return false;
        }
      }

      if (!requiredOnEarly && !requiredOnRecurring) {
        if (methods.length === 0) return false;
      }

      return true;
    });

  return <StepperFooter nextButtonEnabled={allSatisfied} />;
}
